import { ApplicationStatusEnum } from '../../../../common/general-types/briefs.dto';
import { isAutoDeclineExpired, toEpochMs } from './molecules/application-expiry-bar/application-expiry-bar.helpers';
import { formatStageElapsed } from './molecules/application-stage-progress/application-stage-progress.helpers';

/**
 * View model for the redesigned Assignment tab.
 *
 * Everything the table, cards and side panel need to decide — which of the six stages a
 * creator is at, whether they need the brand, and what the one next step is — lives here
 * so the three surfaces cannot disagree. The stage mapping and the needs-you rule are
 * mirrored by the API (`assignment-summary`, `needsYou` filter and sort); keep them in step.
 */

export type BriefKind = 'ugc' | 'influence';

/** Hired this long without starting reads as late, and needs the brand. */
export const ASSIGNMENT_LATE_AFTER_DAYS = 7;

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

export interface AssignmentRowInput {
  applicationStatus: ApplicationStatusEnum;
  applicationStatusModifiedDate?: string | Date | null;
  createdDate?: string | Date | null;
  autoDeclineAt?: string | Date | null;
  /** The API sends a status → count map (0 New, 1 Seen, 2 Accepted, 3 Declined). */
  videos?: unknown;
  statusHistory?: readonly { status: number; createdDate: string | Date }[] | null;
  /** Set by the API when the brand has reviewed this creator for this brief. */
  rated?: boolean;
  /** Set by the API from the same rule as `needsYou` below. */
  needsYou?: boolean;
}

interface BriefKindInput {
  shoutBrief?: unknown;
  pieces?: readonly { placements?: readonly { kind: string; channel?: string }[] }[] | null;
}

function firstSocialPlacement(brief: BriefKindInput | null | undefined) {
  for (const piece of brief?.pieces ?? []) {
    const social = piece.placements?.find((placement) => placement.kind === 'social');
    if (social) return social;
  }
  return null;
}

/** Influence when the brief asks for at least one social post: a shout brief, or a social piece. */
export function briefKindFor(brief: BriefKindInput | null | undefined): BriefKind {
  return brief?.shoutBrief || firstSocialPlacement(brief) ? 'influence' : 'ugc';
}

const PLACEMENT_CHANNEL_NAME: Record<string, string> = {
  tiktok_post: 'TikTok',
  instagram_reel: 'Instagram',
  instagram_story: 'Instagram',
  youtube_shorts: 'YouTube',
  snapchat_spotlight: 'Snapchat',
  snapchat_story: 'Snapchat',
};

/** Network name of a piece list's first social placement, for briefs without a shout brief. */
export function socialChannelName(brief: BriefKindInput | null | undefined): string {
  const channel = firstSocialPlacement(brief)?.channel;
  return channel ? PLACEMENT_CHANNEL_NAME[channel] ?? '' : '';
}

export function assignmentStageLabels(kind: BriefKind): string[] {
  return ['Invited', 'Applied', 'Hired', 'Started', kind === 'influence' ? 'Posting' : 'Delivered', 'Completed'];
}

const STAGE_BY_STATUS = new Map<number, number>([
  [ApplicationStatusEnum.Invited, 0],
  [ApplicationStatusEnum.Applied, 1],
  [ApplicationStatusEnum.Hired, 2],
  [ApplicationStatusEnum.Started, 3],
  [ApplicationStatusEnum.ReadyToPost, 4],
  [ApplicationStatusEnum.Posted, 5],
  [ApplicationStatusEnum.Completed, 5],
  [ApplicationStatusEnum.Paid, 5],
  [ApplicationStatusEnum.BonusPaid, 5],
]);

/** The design's stage (0 Invited … 5 Completed), or null for a status the tab hides. */
export function assignmentStageIndex(status: ApplicationStatusEnum): number | null {
  return STAGE_BY_STATUS.get(status) ?? null;
}

/** Statuses that make up each design stage, for the tile filters. */
export const ASSIGNMENT_STAGE_STATUSES: readonly ApplicationStatusEnum[][] = [
  [ApplicationStatusEnum.Invited],
  [ApplicationStatusEnum.Applied],
  [ApplicationStatusEnum.Hired],
  [ApplicationStatusEnum.Started],
  [ApplicationStatusEnum.ReadyToPost],
  [ApplicationStatusEnum.Posted, ApplicationStatusEnum.Completed, ApplicationStatusEnum.Paid, ApplicationStatusEnum.BonusPaid],
];

export interface VideoCounts {
  new: number;
  seen: number;
  done: number;
}

export function videoCounts(row: Pick<AssignmentRowInput, 'videos'>): VideoCounts {
  const map = row.videos && typeof row.videos === 'object' && !Array.isArray(row.videos)
    ? (row.videos as Record<string, unknown>)
    : {};
  const read = (key: string) => {
    const value = Number(map[key]);
    return Number.isFinite(value) ? value : 0;
  };
  return { new: read('0'), seen: read('1'), done: read('2') };
}

/** When the creator entered their current status: first matching history event, else the modified date. */
export function stageEnteredAtMs(row: AssignmentRowInput): number | null {
  let earliest: number | null = null;
  for (const event of row.statusHistory ?? []) {
    if (event.status !== row.applicationStatus) {
      continue;
    }
    const ms = toEpochMs(event.createdDate);
    if (ms != null && (earliest == null || ms < earliest)) {
      earliest = ms;
    }
  }
  return earliest ?? toEpochMs(row.applicationStatusModifiedDate) ?? toEpochMs(row.createdDate);
}

/** "3 days ago", or an empty string when the time isn't known. */
export function stageElapsed(row: AssignmentRowInput, nowMs: number): string {
  const entered = stageEnteredAtMs(row);
  return entered == null ? '' : formatStageElapsed(nowMs - entered);
}

export function isLate(row: AssignmentRowInput, nowMs: number): boolean {
  if (row.applicationStatus !== ApplicationStatusEnum.Hired) {
    return false;
  }
  const entered = stageEnteredAtMs(row);
  return entered != null && nowMs - entered >= ASSIGNMENT_LATE_AFTER_DAYS * DAY_MS;
}

/** Whether the brand is the one holding this creator up. Prefers the API's flag. */
export function needsYou(row: AssignmentRowInput, nowMs: number): boolean {
  if (typeof row.needsYou === 'boolean') {
    return row.needsYou;
  }
  switch (assignmentStageIndex(row.applicationStatus)) {
    case 1:
      return !isAutoDeclineExpired(row.autoDeclineAt, nowMs);
    case 2:
      return isLate(row, nowMs);
    case 3:
      return videoCounts(row).new > 0;
    case 4:
      return true;
    case 5:
      return !row.rated;
    default:
      return false;
  }
}

export type NextStepAction = 'remind' | 'hire' | 'view';

export interface NextStep {
  action: NextStepAction;
  label: string;
  primary: boolean;
  /** Applied rows also get a Decline button. */
  decline: boolean;
  why: string;
  sub: string;
}

export interface NextStepContext {
  kind: BriefKind;
  nowMs: number;
  /** Videos each creator owes, for the "1 of 3 approved" line. */
  videosRequired?: number | null;
  /** Primary channel name for influence briefs, e.g. "TikTok". */
  channelLabel?: string;
}

/** The one thing to do next for a creator, and the words that explain it. */
export function nextStep(row: AssignmentRowInput, ctx: NextStepContext): NextStep {
  const ago = stageElapsed(row, ctx.nowMs);
  const view = (why: string, sub: string): NextStep => ({
    action: 'view',
    label: 'View submission',
    primary: needsYou(row, ctx.nowMs),
    decline: false,
    why,
    sub,
  });

  switch (assignmentStageIndex(row.applicationStatus)) {
    case 0:
      return { action: 'remind', label: 'Remind', primary: false, decline: false, why: 'Invited, hasn’t applied yet', sub: `Invited ${ago}` };
    case 1:
      return { action: 'hire', label: 'Hire', primary: true, decline: true, why: 'Review application', sub: `Applied ${ago} · check their screening answers` };
    case 2:
      return isLate(row, ctx.nowMs)
        ? view('Hasn’t started yet', `Hired ${ago}. Most creators start within 3 days.`)
        : view('Waiting to start', `Hired ${ago} · brief sent`);
    case 3: {
      const counts = videoCounts(row);
      if (counts.new > 0) {
        return view(`${counts.new} new ${counts.new === 1 ? 'video' : 'videos'} to review`, `Uploaded ${ago}`);
      }
      const required = ctx.videosRequired && ctx.videosRequired > 0 ? ctx.videosRequired : null;
      return view('Filming', required ? `${counts.done} of ${required} videos approved` : `${counts.done} approved so far`);
    }
    case 4:
      return ctx.kind === 'influence'
        ? view(`Ready to post on ${ctx.channelLabel || 'social'}`, 'Videos approved · check the post matches once it’s live')
        : view('All videos delivered', 'Approve the delivery to release payment');
    case 5:
      return row.rated
        ? view('Completed', 'Paid · you rated this creator')
        : view('Rate this creator', 'Reviews help other brands hire with confidence');
    default:
      return view('Not on this brief', '');
  }
}

export type CountdownLabel = 'Plenty of time' | 'Respond soon' | 'Running out' | 'Time’s up';

export interface CountdownView {
  /** 0–1 of the window left. */
  p: number;
  widthPercent: number;
  hue: number;
  colour: string;
  label: CountdownLabel;
  timeLeft: string;
}

/**
 * The application countdown. Colour is continuous: fully green while 60% or more is left,
 * then through amber to red.
 */
export function countdownView(remainingMs: number, windowMs: number): CountdownView {
  const p = windowMs > 0 ? Math.min(1, Math.max(0, remainingMs / windowMs)) : 0;
  const hue = 25 + 120 * Math.min(1, p / 0.6);
  const lightness = 0.55 + 0.07 * p;
  const label: CountdownLabel =
    p >= 0.5 ? 'Plenty of time' : p >= 0.2 ? 'Respond soon' : p > 0 ? 'Running out' : 'Time’s up';
  return {
    p,
    widthPercent: Math.max(2, p * 100),
    hue,
    colour: `oklch(${round(lightness, 3)} 0.19 ${round(hue, 2)})`,
    label,
    timeLeft: formatTimeLeft(remainingMs),
  };
}

export function formatTimeLeft(remainingMs: number): string {
  if (!(remainingMs > 0)) {
    return '0h left';
  }
  const days = Math.floor(remainingMs / DAY_MS);
  const hours = Math.floor((remainingMs % DAY_MS) / HOUR_MS);
  return days >= 1 ? `${days}d ${hours}h left` : `${hours}h left`;
}

function round(value: number, places: number): number {
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
}
